import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { refreshSession, extractAccessToken } from './authApi';
import { accessTokenStore } from './accessTokenStore';
import { isRememberMeEnabled } from './cookies';
import { accessTokenPersistOptions } from './tokenPersist';
import { onApiSuccess, onSessionExpired } from './sessionEvents';
import { useAuthToken } from './AuthTokenProvider';
import './SessionContinueModal.css';

const WARN_MS = 30 * 60 * 1000;

/**
 * rememberMe=false: timer resets on each API success → Continue? → Yes refresh / No login.
 * rememberMe=true: no Continue? timer; 401 → silent refresh (httpClient).
 */
function SessionProvider({ children }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuthToken();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const rememberMeFlag = useSelector((state) => state.auth.rememberMe);
  const userId = useSelector((state) => state.auth.user?.userId ?? null);

  const [showContinue, setShowContinue] = useState(false);
  const timerRef = useRef(null);
  const deadlineRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    deadlineRef.current = null;
  }, []);

  const forceLogout = useCallback(async () => {
    clearTimer();
    setShowContinue(false);
    await logout();
    navigate('/login', { replace: true });
  }, [clearTimer, logout, navigate]);

  const startTimer = useCallback(() => {
    if (!isAuthenticated) return;

    // Long session: no Continue? popup.
    if (isRememberMeEnabled(rememberMeFlag)) {
      clearTimer();
      setShowContinue(false);
      return;
    }

    clearTimer();
    deadlineRef.current = Date.now() + WARN_MS;
    timerRef.current = setTimeout(() => {
      deadlineRef.current = null;
      setShowContinue(true);
    }, WARN_MS);
  }, [clearTimer, isAuthenticated, rememberMeFlag]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimer();
      setShowContinue(false);
      return undefined;
    }

    startTimer();
    const offSuccess = onApiSuccess(() => {
      // Only reset the short-session timer.
      if (!isRememberMeEnabled(rememberMeFlag)) {
        startTimer();
      }
    });
    const offExpired = onSessionExpired(() => {
      void forceLogout();
    });

    return () => {
      offSuccess();
      offExpired();
      clearTimer();
    };
  }, [isAuthenticated, rememberMeFlag, startTimer, clearTimer, forceLogout]);

  const handleContinueYes = async () => {
    try {
      const res = await refreshSession({ uid: userId });
      const token = extractAccessToken(res);
      if (!token) throw new Error('No accessToken from refresh');
      accessTokenStore.set(token, accessTokenPersistOptions(rememberMeFlag));
      setShowContinue(false);
      startTimer();
    } catch {
      await forceLogout();
    }
  };

  const handleContinueNo = async () => {
    await forceLogout();
  };

  return (
    <>
      {children}
      {showContinue && (
        <div className="session-continue-overlay" role="dialog" aria-modal="true">
          <div className="session-continue-modal">
            <p className="session-continue-message">{t('session.continuePrompt')}</p>
            <div className="session-continue-actions">
              <button type="button" className="session-continue-yes" onClick={handleContinueYes}>
                {t('session.continueYes')}
              </button>
              <button type="button" className="session-continue-no" onClick={handleContinueNo}>
                {t('session.continueNo')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SessionProvider;
